import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IconX } from "@tabler/icons-react";
import JuisciLogo from "../../assets/core/juisci-logo.svg?react";
import { useIsDesktopView } from "../../common/hooks/useIsDesktopView";
import { cn } from "../../lib/utils";
import { PracticeProfilesEditor } from "../../components/practice/PracticeProfilesEditor";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { getUserHomeRoute } from "../../domains/app/utils/getInitialRoute";
import {
  settleHomeDestination,
  useHomeSurface,
} from "../../domains/app/hooks/useHomeSurface";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { claimOncePerSession } from "../../tools/analytics/eventIdentity";
import {
  gaEventPracticeProfilingGateDismissed,
  gaEventPracticeProfilingGateShown,
} from "../../tools/analytics/domains/onboarding.analytics";

/**
 * The practice question as a full PAGE (`/onboarding/practice`) — the practice
 * arm's Explore gate, and the exact mirror of {@link InterestsProfilingPage}.
 *
 * `AppRoutes` routes every `/discovery*` request here while the practitioner
 * has never confirmed a practice. Same place in the funnel as the topics
 * conversation the control arm walks, so the two arms are comparable; without
 * it the arm targets accounts the server LENDS a practice to, and the
 * confirmation rate measures a guess.
 *
 * The cross and back navigation always leave, even while the answer is owed:
 * the practitioner returns to JuisciGPT, and Explore stays gated. Nothing is
 * stamped, so the question comes back on the next Explore request.
 *
 * No navbar, like the topics page, which is why the cross has to exist at all.
 */
export default function PracticeProfilingPage(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { isDesktop } = useIsDesktopView();
  // Read reactively, in the body: the handler below is redefined every
  // render, so by the time the cross is pressed it closes over the settled
  // value (`.knowledge/growthbook-oneshot-flag-race.md`).
  const homeSurface = useHomeSurface();
  const specialtyUid =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;

  // The editor's search field sits in the bottom band, above the CTA.
  useKeyboardManaged();

  // The gate re-presents itself on every Explore request until the
  // practitioner answers, and each mount is the same practitioner meeting the
  // same wall. `useTrackOnMount` owns that session scope now.
  useTrackOnMount(
    "practice-profiling-gate-shown",
    specialtyUid === null
      ? { status: "hidden" }
      : { status: "shown", key: specialtyUid },
    ({ key: specialty_uid }) => {
      gaEventPracticeProfilingGateShown({ specialty_uid });
    }
  );

  // `goBack` is what the cross means: return where they came from. The router
  // reached this page by REPLACING the `/discovery` request, so the previous
  // entry is whatever they were doing before asking for Explore, never the
  // gated route itself. `history.length === 1` means this entry is the
  // session's first and there is nothing behind it — a cold boot straight onto
  // the gate, a fresh native webview.
  function close(): void {
    // Reported before navigating, and above BOTH ways out: the cold-boot branch
    // replaces this entry, so anything hung off the unmount would be racing the
    // navigation it describes.
    // The same session scope, and the same key, `useTrackOnMount` gives the
    // gate's own impression: the two are a numerator and a denominator, and a
    // numerator counted per mount against a denominator counted per session
    // produces a rate above 1. It means "closed the question at least once this
    // session", never "refused for good".
    if (
      specialtyUid !== null &&
      claimOncePerSession(`practice-profiling-gate-dismissed:${specialtyUid}`)
    ) {
      gaEventPracticeProfilingGateDismissed({ specialty_uid: specialtyUid });
    }
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.replace(
      currentUser === null
        ? "/"
        : getUserHomeRoute(settleHomeDestination(homeSurface, currentUser))
    );
  }

  return (
    <KeyboardAvoidingView
      behavior="padding"
      enabled={!isDesktop}
      className={cn(
        "flex h-dvh flex-col bg-ds-cream",
        isDesktop
          ? "items-center justify-center p-8"
          : "pt-(--safe-area-top) pb-(--safe-area-bottom)"
      )}
    >
      <main
        aria-labelledby="practice-profiling-title"
        className={cn(
          "flex min-h-0 w-full flex-1 flex-col overflow-y-auto",
          isDesktop
            ? "max-h-224 max-w-2xl rounded-2xl border border-ds-slate-200 bg-ds-white px-7 py-6"
            : null
        )}
      >
        {/* Keep space for the list when the keyboard or window reduces height.
            The panel scrolls once the complete form cannot fit. */}
        <div className="flex min-h-120 flex-1 flex-col">
          <div
            className={cn(
              "flex shrink-0 items-center",
              isDesktop
                ? "justify-between px-5 pb-6"
                : "h-14 justify-end px-3.5"
            )}
          >
            {isDesktop && <JuisciLogo className="h-8 w-auto" aria-hidden />}
            <button
              type="button"
              onClick={close}
              aria-label={t("Close")}
              className={cn(
                "flex size-8 items-center justify-center rounded-full",
                isDesktop
                  ? "bg-ds-slate-50 hover:bg-ds-slate-100"
                  : "bg-ds-white"
              )}
            >
              <IconX className="size-5 text-ds-mint-900" />
            </button>
          </div>
          <h1
            id="practice-profiling-title"
            className={cn(
              "m-0 shrink-0 px-5 font-semibold tracking-tight text-ds-black",
              isDesktop
                ? "pb-4 text-heading-display"
                : "pt-2 pb-3 text-2xl/tight"
            )}
          >
            {t("What do you practise?")}
          </h1>
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col",
              isDesktop ? null : "pb-5"
            )}
          >
            <PracticeProfilesEditor
              // The invite and the Explore gate share the campaign's selection.
              seedFromInference
              surface="sheet"
              onSaved={() => {
                history.replace("/discovery");
              }}
            />
          </div>
        </div>
      </main>
    </KeyboardAvoidingView>
  );
}
